import { Currency, Popover } from "@xola/ui-kit";
import React from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { Seller } from "types";
import { AnalyticsView, useAnalyticsFilterStore } from "../../stores/reports/analyticsFilterStore";
import { NoTranslate } from "../Common/NoTranslate";
import { api } from "@/api/api";
import { useAccountStore } from "@/stores/accountStore";
import { useLocalizationStore } from "@/stores/localizationStore";

export const fetchSeller = async (id: ID | null): Promise<Seller> => {
    const { data: seller } = await api.get<Seller>(`/sellers/${id}`);

    return seller;
};

export const SellerBalance = () => {
    const { seller: sellerId } = useAccountStore.getState();
    const { data: seller } = useQuery<Seller>(["sellerBalance"], async () => fetchSeller(sellerId));
    const desiredAmount = seller?.preferences?.payout?.escrow?.desiredAmount ?? 0;

    if (!seller) {
        return null;
    }

    return <div>{desiredAmount > 0 ? <PayoutMessage seller={seller} /> : <BalanceLine seller={seller} />}</div>;
};

const BalanceLine = ({ seller }: { seller: Seller }) => {
    const history = useHistory();
    const locale = useLocalizationStore().getLocale();

    const setFilters = useAnalyticsFilterStore((state) => state.setFilters);
    const handleBalanceClick = () => {
        setFilters({ view: AnalyticsView.TABLE });
        history.push("/reports/customReports/new/?object=TransactionsReport&preview=true");
    };

    return (
        <span
            className="cursor-pointer font-bold tracking-tightest underline underline-offset-2"
            onClick={handleBalanceClick}
        >
            <span>Balance:</span>
            <var data-var="sellerBalance">
                <NoTranslate>
                    <Currency locale={locale} currency={seller.currency}>
                        {seller.balance ?? 0}
                    </Currency>
                </NoTranslate>
            </var>
        </span>
    );
};

const PayoutMessage = ({ seller }: { seller: Seller }) => {
    const desiredAmount = seller.preferences?.payout?.escrow?.desiredAmount ?? 0;
    const reserveActualAmount = seller.preferences?.payout?.escrow?.actualAmount ?? 0;
    const balance = seller.balance ?? 0;
    const availableToPayout = Math.max(balance - reserveActualAmount, 0);

    const refundReserve = balance < reserveActualAmount ? balance : reserveActualAmount;

    return (
        <Popover className="w-56 rounded-lg !border-black !bg-black">
            <span className="cursor-pointer">
                <BalanceLine seller={seller} />
            </span>
            <Popover.Content>
                <div className="space-y-1 p-3 text-sm text-white">
                    <h5 className="font-bold">Payout</h5>
                    <div>Your balance less the refund reserve is what is available to payout.</div>
                    <table className="w-full text-gray" cellPadding={2}>
                        <tbody>
                            <tr>
                                <td>Balance</td>
                                <td className="text-right">
                                    <CurrencyDisplay value={balance} seller={seller} />
                                </td>
                            </tr>
                            <tr>
                                <td>Refund Reserve</td>
                                <td className="text-right">
                                    -
                                    <CurrencyDisplay
                                        shouldRemoveTrailingZeroes={false}
                                        value={refundReserve}
                                        seller={seller}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} className="bottom-line">
                                    <hr className="text-gray-darker" />
                                </td>
                            </tr>
                            <tr>
                                <td className="text-right font-bold text-white">
                                    <strong>Available to payout</strong>
                                </td>
                                <td className="text-right font-bold text-white">
                                    <CurrencyDisplay
                                        shouldRemoveTrailingZeroes={false}
                                        value={availableToPayout}
                                        seller={seller}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {refundReserve < desiredAmount && (
                        <div className="text-sm">
                            Funds will become available to payout once your refund reserve minimum (
                            <CurrencyDisplay value={desiredAmount} seller={seller} />) is met.
                        </div>
                    )}
                </div>
            </Popover.Content>
        </Popover>
    );
};

interface CurrencyDisplayProps {
    value: number;
    seller: Seller;
    shouldRemoveTrailingZeroes?: boolean;
}

const CurrencyDisplay = ({ seller, value, shouldRemoveTrailingZeroes = true }: CurrencyDisplayProps) => {
    const locale = useLocalizationStore().getLocale();

    return (
        <NoTranslate>
            <Currency
                shouldRemoveTrailingZeroes={shouldRemoveTrailingZeroes}
                currency={seller.currency}
                locale={locale}
            >
                {value}
            </Currency>
        </NoTranslate>
    );
};
